// PerformanceCard · Lot M5
// Bloc cockpit "Suivi hebdomadaire" : cadence, prochaine revue, badge en retard,
// CTA "Nouveau snapshot" et "Changer la cadence".

import { LineChart, Sparkles, Timer, AlertTriangle, Clock, Settings2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const fmtRelative = (iso) => {
  if (!iso) return null;
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMs = t - now;
  const abs = Math.abs(diffMs);
  const day = 86_400_000;
  const hour = 3_600_000;
  const min = 60_000;
  const past = diffMs < 0;
  if (abs < min)  return past ? "à l'instant" : "dans un instant";
  if (abs < hour) return `${past ? "il y a " : "dans "}${Math.round(abs / min)} min`;
  if (abs < day)  return `${past ? "il y a " : "dans "}${Math.round(abs / hour)} h`;
  return `${past ? "il y a " : "dans "}${Math.round(abs / day)} j`;
};

export default function PerformanceCard({
  summary,        // { first_published_at, review_frequency_days, next_performance_review_at,
                  //   is_overdue, last_snapshot_id, last_snapshot_at, last_snapshot_period_index,
                  //   current_period: { period_index, period_start, period_end } | null }
  isTerminal,
  onNewSnapshot,
  onChangeFrequency,
  hasPublished,   // bool — mandat déjà publié (first_published_at existant)
}) {
  const notPublishedYet = !hasPublished;
  const freq = summary?.review_frequency_days ?? 7;
  const isOverdue = !!summary?.is_overdue;
  const nextAt = summary?.next_performance_review_at;
  const currentPeriod = summary?.current_period || null;
  const now = Date.now();
  const periodEnd = currentPeriod?.period_end
    ? new Date(currentPeriod.period_end).getTime() : null;
  const periodReady = periodEnd != null && now >= periodEnd;
  const canCapture = !isTerminal && hasPublished && periodReady && currentPeriod;

  return (
    <section
      className="rounded-3xl bg-white/70 p-7"
      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
      data-testid="performance-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
            <LineChart size={16} strokeWidth={1.7} className="text-black/60" />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-black">
              Suivi hebdomadaire
            </h2>
            <p className="mt-1 text-[12.5px] text-black/50 max-w-[460px]">
              Snapshots hebdomadaires immuables — cadence configurable,
              séparation stricte stats plateformes / activité commerciale.
            </p>
          </div>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
          M5
        </span>
      </div>

      {notPublishedYet ? (
        <div
          className="mt-6 rounded-2xl bg-black/[0.03] px-5 py-4 text-[13px] text-black/55"
          data-testid="performance-not-published"
        >
          Le suivi démarre à la <b className="text-black">première publication</b> d&apos;un canal.
          Aucun snapshot n&apos;est possible avant.
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div
            className="rounded-2xl bg-black/[0.03] px-5 py-4"
            data-testid="perf-tile-frequency"
          >
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-black/45">
              <Timer size={11} /> Cadence
            </div>
            <div className="mt-1 font-mono text-[22px] tracking-[-0.01em] text-black">
              {freq} j
            </div>
            <button
              onClick={onChangeFrequency}
              disabled={isTerminal}
              className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-black/50 hover:text-black disabled:opacity-30"
              data-testid="perf-change-frequency-btn"
            >
              <Settings2 size={11} /> Modifier
            </button>
          </div>

          <div
            className={`rounded-2xl px-5 py-4 ${
              isOverdue ? "bg-[#FEF2F2]" : "bg-black/[0.03]"
            }`}
            data-testid="perf-tile-next-review"
          >
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-black/45">
              {isOverdue ? <AlertTriangle size={11} className="text-[#B91C1C]" />
                          : <Clock size={11} />}
              Prochaine revue
            </div>
            <div className={`mt-1 font-mono text-[18px] tracking-[-0.01em] ${
              isOverdue ? "text-[#B91C1C]" : "text-black"
            }`}>
              {nextAt ? fmtRelative(nextAt) : "—"}
            </div>
            <div className="mt-0.5 text-[11.5px] text-black/40">
              {nextAt ? fmtDate(nextAt) : "en attente 1re publication"}
            </div>
            {isOverdue && (
              <div
                className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#B91C1C]/10 px-2.5 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[#B91C1C]"
                data-testid="perf-overdue-badge"
              >
                En retard
              </div>
            )}
          </div>

          <div
            className="rounded-2xl bg-black/[0.03] px-5 py-4"
            data-testid="perf-tile-last-snapshot"
          >
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-black/45">
              <LineChart size={11} /> Dernier snapshot
            </div>
            <div className="mt-1 font-mono text-[18px] tracking-[-0.01em] text-black">
              {summary?.last_snapshot_period_index
                ? `Période #${summary.last_snapshot_period_index}` : "—"}
            </div>
            <div className="mt-0.5 text-[11.5px] text-black/40">
              {summary?.last_snapshot_at
                ? `capturé ${fmtRelative(summary.last_snapshot_at)}`
                : "aucune capture encore"}
            </div>
          </div>
        </div>
      )}

      {!notPublishedYet && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-black/[0.03] px-5 py-3">
          <div className="text-[12.5px] text-black/55">
            {currentPeriod ? (
              <>
                Période courante&nbsp;
                <b className="text-black">#{currentPeriod.period_index}</b>
                &nbsp;•&nbsp;
                du <b className="text-black">{fmtDate(currentPeriod.period_start)}</b>
                &nbsp;au <b className="text-black">{fmtDate(currentPeriod.period_end)}</b>
                {periodReady ? (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#C8FF2F]/40 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.14em] text-black">
                    prête
                  </span>
                ) : (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-black/10 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.14em] text-black/50">
                    en cours
                  </span>
                )}
              </>
            ) : "Aucune période à capturer pour l'instant."}
          </div>
          <button
            onClick={onNewSnapshot}
            disabled={!canCapture}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-[#C8FF2F] px-4 py-2 text-[12.5px] font-medium text-black transition-transform hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100"
            data-testid="perf-new-snapshot-btn"
            title={
              !hasPublished ? "Publier au moins un canal d'abord"
              : isTerminal ? "Mandat fermé"
              : !periodReady ? "La période courante n'est pas terminée"
              : ""
            }
          >
            <Sparkles size={13} /> Nouveau snapshot
          </button>
        </div>
      )}
    </section>
  );
}
